import React, { useState } from 'react';
import {
  Box,
  Button,
  ButtonBase,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Popover,
  Typography,
  useMediaQuery,
  useTheme
} from '@material-ui/core';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useTranslation } from 'react-i18next';
import Desc from '../../FormWidgets/Description';
import { CustomizableInfoValueRow } from '../../FormWidgets/InfoValueList';
import { NormalValue } from '../../FormWidgets/InfoValues';
import LightProfilesOverview from '../../components/LightProfilesOverview';
import { channelKey } from '../../Utils/light_profiles';
import { getLightProfileColour } from '../../Utils/light_profile_colours';
import { isZoned } from '../../Utils/utils';
import { lightingInfoView, lightInfoStatus, lightInfoValueId } from '../../Utils/light_info_utils';

function ChannelOutput({ row }) {
  const { t } = useTranslation();
  const status = row.status;
  return (
    <>
      <NormalValue name={lightInfoValueId(row)} value={status ? status.actual / 10 : -1} unit="%" />
      {status && status.requested !== status.actual && (
        <Typography variant="caption" component="div">
          {t('lighting_info_requested', { value: status.requested / 10 })}
        </Typography>
      )}
    </>
  );
}

// The compact legacy values retain their identifiers/visibility. Channel pins
// join that same customization flow, hidden until the user chooses them.
export default function LightingInfoOutputs({ controller, parent, snapshot, settings, infos }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [opened, setOpened] = useState(null);
  const view = lightingInfoView(snapshot, { zone_id: parent.zone_id, settings, infos });
  const close = () => setOpened(null);
  const rowIds = [
    'info_lights',
    'lights_dim_val_current_2',
    'lights_dim_val_current_3',
    'lights_dim_val_current_4'
  ];
  const shared = view?.sharedToplight;
  const relevant = (row) =>
    row.subzone === opened?.subzone || (shared && row.subzone === 0 && row.channel === 0);
  const rows = view?.rows.filter(relevant) ?? [];
  const title = t('lighting_info_details', { number: (opened?.subzone ?? 0) + 1 });
  const details = view && (
    <Box p={2} style={{ width: mobile ? '100%' : 580, maxWidth: '100%', boxSizing: 'border-box' }}>
      {!mobile && (
        <Typography variant="h6" component="h2">
          {title}
        </Typography>
      )}
      <Typography variant="caption" component="div" align="right" color="textSecondary">
        {t('lighting_info_output')}
      </Typography>
      {rows.length ? (
        rows.map((row) => (
          <Box
            key={channelKey(row)}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            py={1}
          >
            <Box pr={2} style={{ minWidth: 0 }}>
              <Typography style={{ overflowWrap: 'anywhere' }}>
                <span
                  aria-hidden="true"
                  style={{ color: getLightProfileColour(`${controller.id}:${channelKey(row)}`) }}
                >
                  ●{' '}
                </span>
                {row.name}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {lightInfoStatus(row.status, t)}
              </Typography>
            </Box>
            <Box textAlign="right" style={{ whiteSpace: 'nowrap' }}>
              <ChannelOutput row={row} />
            </Box>
          </Box>
        ))
      ) : (
        <Typography>{t('lighting_info_no_channels')}</Typography>
      )}
      {view.chart ? (
        <LightProfilesOverview
          {...view.chart}
          controllerId={controller.id}
          channels={view.chart.sourceChannels.filter(relevant)}
          compact
          readOnly
        />
      ) : (
        <Typography variant="body2" color="textSecondary">
          {t('lighting_info_profile_unavailable')}
        </Typography>
      )}
      {!mobile && (
        <Box textAlign="right">
          <Button onClick={close}>{t('close')}</Button>
        </Box>
      )}
    </Box>
  );
  return (
    <>
      {rowIds.map((id, subzone) => {
        if (subzone && (!isZoned(controller) || !(infos.lights_dim_show_cnt > subzone)))
          return null;
        const value = <NormalValue name={id} value={infos[id] ?? -1} unit="%" />;
        return (
          <CustomizableInfoValueRow
            key={id}
            controller={controller}
            parent={parent}
            id={id}
            extId={`edit_${id}`}
            label={<Desc id={id} />}
          >
            {view ? (
              <ButtonBase
                aria-label={t('lighting_info_details', { number: subzone + 1 })}
                aria-haspopup="dialog"
                onClick={(event) => setOpened({ subzone, anchor: event.currentTarget })}
                style={{ minHeight: 44, minWidth: 44, gap: 4, font: 'inherit' }}
              >
                {value}
                <ChevronRightIcon fontSize="small" />
              </ButtonBase>
            ) : (
              value
            )}
          </CustomizableInfoValueRow>
        );
      })}
      {view?.rows.map((row) => (
        <CustomizableInfoValueRow
          key={channelKey(row)}
          controller={controller}
          parent={parent}
          id={lightInfoValueId(row)}
          defaultVisible={false}
          label={row.name}
          valueDetail={
            <Typography variant="caption" color="textSecondary">
              {lightInfoStatus(row.status, t)}
            </Typography>
          }
        >
          <ChannelOutput row={row} />
        </CustomizableInfoValueRow>
      ))}
      {mobile ? (
        <Dialog
          open={Boolean(opened && view)}
          onClose={close}
          fullWidth
          maxWidth="sm"
          aria-labelledby="lighting-info-title"
        >
          <DialogTitle id="lighting-info-title">{title}</DialogTitle>
          <DialogContent style={{ padding: 0 }}>{details}</DialogContent>
          <DialogActions>
            <Button onClick={close}>{t('close')}</Button>
          </DialogActions>
        </Dialog>
      ) : (
        <Popover
          open={Boolean(opened && view)}
          anchorEl={opened?.anchor}
          onClose={close}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{ role: 'dialog', 'aria-label': title }}
        >
          {details}
        </Popover>
      )}
    </>
  );
}
